import { useTranslation } from "react-i18next";
import { BadgeCheck, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CreatorReputation } from "@/features/bounty/CreatorReputation";
import { presenceLabel, type InviteRole, type MemberPresence, type OrgKind, type OrgRole, type PostedAsOrg } from "@/lib/organizations";

/** The org's logo, or a neutral building tile. Square-rounded so it never reads as a person's avatar. */
export function OrgLogo({
  url,
  name,
  size = "md",
  className,
}: {
  url: string | null;
  name: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const box = { sm: "h-8 w-8", md: "h-10 w-10", lg: "h-14 w-14", xl: "h-20 w-20 sm:h-24 sm:w-24" }[size];
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border-subtle bg-surface-secondary",
        box,
        className,
      )}
    >
      {url ? (
        <img src={url} alt={name} className="h-full w-full object-cover" />
      ) : (
        <Building2 className="h-1/2 w-1/2 text-text-muted" aria-hidden />
      )}
    </div>
  );
}

export function VerifiedBadge({ className, compact }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn("inline-flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300", className)}
      title={t("orgs.verifiedHint")}
    >
      <BadgeCheck className="h-4 w-4" aria-hidden />
      {compact ? <span className="sr-only">{t("orgs.verified")}</span> : t("orgs.verified")}
    </span>
  );
}

export function OrgKindLabel({ kind }: { kind: OrgKind }) {
  const { t } = useTranslation();
  return <>{t(`orgs.kind.${kind}`)}</>;
}

/** Every level gets a chip (Simple and Medio too); the strongest reads loudest. */
const ROLE_CHIP: Record<OrgRole, string> = {
  owner: "bg-brand-50 text-brand-700 dark:bg-brand-500/20 dark:text-brand-200",
  admin: "bg-surface-tertiary text-text-secondary",
  member: "bg-surface-secondary text-text-secondary",
  simple: "border border-border-subtle text-text-tertiary",
};

export function RoleBadge({ role }: { role: OrgRole }) {
  const { t } = useTranslation();
  return (
    <span className={cn("rounded-chip px-2 py-0.5 text-[11px] font-medium", ROLE_CHIP[role])}>
      {t(`orgs.role.${role}`)}
    </span>
  );
}

/** The invite level picker, lowest level first; each option explains itself on hover. */
export function RoleSelect({ value, options, onChange }: {
  value: InviteRole;
  options: readonly InviteRole[];
  onChange: (role: InviteRole) => void;
}) {
  const { t } = useTranslation();
  return (
    <select aria-label={t("orgs.inviteRole")} value={value}
      onChange={(e) => onChange(e.target.value as InviteRole)}
      className="h-10 rounded-input border border-border-default bg-surface px-2 text-sm text-text-primary">
      {options.map((r) => (
        <option key={r} value={r} title={t(`orgs.roleDesc.${r}`)}>{t(`orgs.role.${r}`)}</option>
      ))}
    </select>
  );
}

/** Green dot + "Active now", or "Active 3h ago"; nothing when hidden. */
export function PresenceIndicator({ presence }: { presence: MemberPresence | undefined }) {
  const { t } = useTranslation();
  const label = presenceLabel(presence);
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-text-tertiary">
      <span
        className={cn("h-2 w-2 rounded-full", label.online ? "bg-success-500" : "bg-border-default")}
        aria-hidden
      />
      {t(label.key, { count: label.count })}
    </span>
  );
}

/**
 * A card's "Posted by" block when the quest was posted as an org
 * (organizations PR 2): the org leads, the member who posted it follows as
 * "via". Plain text — cards are buttons, so no nested link.
 */
export function OrgPosterLine({
  org,
  creatorName,
  creatorId,
}: {
  org: PostedAsOrg;
  creatorName: string | null;
  /** The member's ledger reputation stays theirs (spec §6), shown on the "via" line. */
  creatorId?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 flex items-center gap-2 border-t border-border-subtle pt-3">
      <OrgLogo url={org.logoUrl} name={org.name} size="sm" />
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{t("bounty.card.postedBy")}</p>
        <p className="flex min-w-0 items-center gap-1 text-xs text-text-secondary">
          <span className="truncate">{org.name}</span>
          {org.verified && <VerifiedBadge compact className="shrink-0" />}
        </p>
        {creatorName && (
          <p className="truncate text-[11px] text-text-muted">
            {t("orgs.postedVia", { name: creatorName })}
            <CreatorReputation creatorId={creatorId ?? null} variant="inline" />
          </p>
        )}
      </div>
    </div>
  );
}
